import React, { useState, useRef, useEffect } from 'react';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
    content: React.ReactNode;
    children: React.ReactNode;
    position?: TooltipPosition;
    className?: string;
    interactive?: boolean;
}

export default function Tooltip({ content, children, position = 'top', className = '', interactive = false }: TooltipProps) {
    const [isVisible, setIsVisible] = useState(false);
    const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const show = () => {
        if (hideTimeout.current) clearTimeout(hideTimeout.current);
        setIsVisible(true);
    };

    useEffect(() => {
        return () => {
            if (hideTimeout.current) clearTimeout(hideTimeout.current);
        };
    }, []);

    const hide = () => {
        if (interactive) {
            hideTimeout.current = setTimeout(() => setIsVisible(false), 150);
        } else {
            setIsVisible(false);
        }
    };

    const positionClasses = {
        top: 'bottom-full left-1/2 mb-2 -translate-x-1/2',
        bottom: 'top-full left-1/2 mt-2 -translate-x-1/2',
        left: 'right-full top-1/2 mr-2 -translate-y-1/2',
        right: 'left-full top-1/2 ml-2 -translate-y-1/2'
    };

    const arrowClasses = {
        top: 'top-full left-1/2 -translate-x-1/2 border-t-gray-700 border-b-transparent border-x-transparent',
        bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-700 border-t-transparent border-x-transparent',
        left: 'left-full top-1/2 -translate-y-1/2 border-l-gray-700 border-r-transparent border-y-transparent',
        right: 'right-full top-1/2 -translate-y-1/2 border-r-gray-700 border-l-transparent border-y-transparent'
    };

    return (
        <div
            className={`relative inline-flex items-center ${className}`}
            onMouseEnter={show}
            onMouseLeave={hide}
        >
            {children}
            {isVisible && (
                <div
                    className={`absolute z-50 w-max max-w-sm px-3 py-2 text-xs font-medium text-white bg-gray-700 rounded-lg shadow-xl transform animate-in fade-in zoom-in-95 duration-200 break-words ${interactive ? '' : 'pointer-events-none'} ${positionClasses[position]}`}
                    onMouseEnter={interactive ? show : undefined}
                    onMouseLeave={interactive ? hide : undefined}
                >
                    {content}
                    <div className={`absolute border-4 ${arrowClasses[position]}`}></div>
                </div>
            )}
        </div>
    );
}
